import { useState, useRef, useCallback } from 'react';
import { useTranslation } from '../../../i18n/TranslationContext';
import type { Creator } from '../../../mocks/creators';

function formatFollowers(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

interface CreatorCardProps {
  creator: Creator;
  getName: (c: Creator) => string;
  getDesc: (c: Creator) => string;
}

export default function CreatorCard({ creator, getName, getDesc }: CreatorCardProps) {
  const { t } = useTranslation();
  const [imageIdx, setImageIdx] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const imageList = [1, 2, 3, 4].map((i) => `/images/creators/${creator.folderName}/${i}.webp`);

  const handleMouseEnter = useCallback(() => {
    if (imageList.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setImageIdx((prev) => (prev + 1) % imageList.length);
    }, 900);
  }, [imageList.length]);

  const handleMouseLeave = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setImageIdx(0);
  }, []);

  return (
    <div className="group bg-background-50 rounded-2xl border border-foreground-100/60 overflow-hidden transition-all duration-300 hover:border-primary-300/60">
      <div
        className="relative w-full aspect-[4/5] overflow-hidden cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="w-full h-full relative">
          {imageList.map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt={`${getName(creator)} ${idx + 1}`}
              className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ease-in-out"
              style={{ opacity: idx === imageIdx ? 1 : 0 }}
              loading={idx === 0 ? 'eager' : 'lazy'}
            />
          ))}
        </div>

        {imageList.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
            {imageList.map((_, idx) => (
              <span
                key={idx}
                className={`block rounded-full transition-all duration-300 ${
                  idx === imageIdx
                    ? 'w-4 h-1.5 bg-background-50'
                    : 'w-1.5 h-1.5 bg-background-50/50'
                }`}
              />
            ))}
          </div>
        )}

        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary-500/85 backdrop-blur-sm text-background-50 text-xs font-semibold whitespace-nowrap">
          {t(`creators.filter_${creator.categories[0]}`)}
        </div>
      </div>

      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-heading text-base md:text-lg text-foreground-950">
            {getName(creator)}
          </h3>
          <span className="flex items-center gap-1 text-xs font-semibold text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0">
            {formatFollowers(creator.followers)} {t('creators.followers')}
          </span>
        </div>

        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
          {getDesc(creator)}
        </p>

        <div className="flex items-center gap-2 pt-3 border-t border-foreground-100/60">
          <a
            href={creator.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="h-9 w-9 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer"
            title={t('creators.instagram')}
          >
            <i className="ri-instagram-line text-base"></i>
          </a>
          <a
            href={creator.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            className="h-9 w-9 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-200 cursor-pointer"
            title={t('creators.tiktok')}
          >
            <i className="ri-tiktok-line text-base"></i>
          </a>
          <a
            href={creator.xiaohongshu}
            target="_blank"
            rel="noopener noreferrer"
            className="h-9 px-3 flex items-center justify-center gap-1.5 rounded-full bg-[#FF2442]/10 text-[#FF2442] hover:bg-[#FF2442] hover:text-background-50 transition-all duration-200 cursor-pointer"
            title={t('creators.xiaohongshu')}
          >
            <span className="text-[11px] font-extrabold leading-none whitespace-nowrap">小红书</span>
          </a>
        </div>
      </div>
    </div>
  );
}